import { useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { publicClubMembershipService } from '@/services/publicClubMembership';
import { cn } from '@/lib/utils';

interface MembershipReceiptLinkProps {
  clubSlug: string;
  membershipId: string;
  hasReceipt: boolean;
  className?: string;
}

export function MembershipReceiptLink({
  clubSlug,
  membershipId,
  hasReceipt,
  className,
}: MembershipReceiptLinkProps) {
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!hasReceipt) return null;

  const openReceipt = async () => {
    if (receiptUrl) {
      window.open(receiptUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    setLoading(true);
    try {
      const res = await publicClubMembershipService.getMembershipReceipt(clubSlug, membershipId);
      if (res.success && res.data?.receiptUrl) {
        setReceiptUrl(res.data.receiptUrl);
        window.open(res.data.receiptUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      toast.error(res.error || 'Receipt not available');
    } catch {
      toast.error('Failed to load receipt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void openReceipt()}
      disabled={loading}
      className={cn(
        'inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline disabled:opacity-60',
        className
      )}
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
      )}
      View receipt
    </button>
  );
}
